import React, { useMemo, useState } from 'react';
import { Link } from 'react-router';
import { ActivityCardSkeleton } from 'components/RecentActivityCarousel/ActivityCardSkeleton';
import { AvatarWithFlag } from 'components/AvatarWithFlag/AvatarWithFlag';
import EliteBadge from 'components/EliteBadge/EliteBadge';
import { Points } from 'components/Points/Points';
import ResultCard from 'screens/Main/Search/Components/ResultCard';
import { RECENT_ACTIVITY_COUNT } from 'constants/home';
import { useActivityCarousel } from 'hooks/useActivityCarousel';
import { useIsMobile } from 'hooks/useIsMobile';
import { Activity, getActivityActionText } from 'types/interfaces/activity';
import { trackEvent } from 'utils/analytics';
import { formatAccessibleTime, formatRelativeTime } from 'utils/relativeTime';
import { feedCardToTile } from './feedCardToTile';
import './RecentActivitySection.css';

// Recent Activity, full width: RECENT_ACTIVITY_COUNT tiles and "View more".
// Each tile is the Search page's card tile (Asana 1219036410957400, Decision
// 1: same import, no copy): hover flips to the details face, click opens the
// shared action menu. The feed keeps its own header row above the card (actor,
// verb) and its own meta row under it (the event's points and relative time,
// never the tile's "N offering"). The want-list banner, "+ Want" pill and
// filter chips that used to live here are gone with that ticket.

const RecentActivitySection: React.FC = () => {
  const { activities, isLoading, error, refetch } = useActivityCarousel({ enableSocket: true });
  const visible = useMemo(() => activities.slice(0, RECENT_ACTIVITY_COUNT), [activities]);
  const isMobile = useIsMobile();

  // One hover face and one open menu at a time, owned here exactly as
  // ResultsGrid owns them on Search. Keyed by the feed item id, which is
  // unique per event even when two events show the same printing.
  const [hoveredKey, setHoveredKey] = useState<string | null>(null);
  const [openMenuKey, setOpenMenuKey] = useState<string | null>(null);

  const handleUserClick = (activity: Activity) => (e: React.MouseEvent) => {
    e.stopPropagation();
    trackEvent('carousel_username_clicked', {
      user_id: activity.user.id,
      username: activity.user.username,
      activity_type: activity.type,
    });
  };

  return (
    <section className="home-activity" data-testid="recent-activity-section" aria-label="Recent activity">
      <div className="home-activity__header">
        <h2 className="home-activity__title">Recent Activity</h2>
        <Link to="/home/activity" className="home-activity__more">
          View more
        </Link>
      </div>

      {error ? (
        <div className="home-activity__empty">
          <p>Unable to load recent activity.</p>
          <button type="button" className="home-activity__retry" onClick={() => refetch()}>
            Retry
          </button>
        </div>
      ) : isLoading ? (
        <div className="home-activity__row" data-testid="activity-row">
          <ActivityCardSkeleton count={RECENT_ACTIVITY_COUNT} />
        </div>
      ) : visible.length === 0 ? (
        <div className="home-activity__empty" data-testid="activity-empty">
          <p>No recent activity yet. Be the first to start trading!</p>
        </div>
      ) : (
        <div className="home-activity__row" data-testid="activity-row" data-count={visible.length}>
          {visible.map((activity) => {
            const key = activity.id;
            const actionText = getActivityActionText(activity.type);
            return (
              <article
                key={key}
                className="home-activity__tile"
                data-testid="home-activity-tile"
                aria-label={`${activity.user.username} ${actionText} ${activity.card.name}`}
              >
                <Link
                  to={`/profile/u/${encodeURIComponent(activity.user.username)}`}
                  className="home-activity__actor"
                  onClick={handleUserClick(activity)}
                >
                  <AvatarWithFlag
                    avatarUrl={activity.user.avatarUrl}
                    username={activity.user.username}
                    countryCode={activity.user.countryCode}
                    size={30}
                    isElite={activity.user.isElite}
                    showAnimatedBorder={activity.user.showAnimatedBorder}
                    borderColor1={activity.user.borderColor1}
                    borderColor2={activity.user.borderColor2}
                  />
                  <span className="home-activity__actor-text">
                    <span className="home-activity__username">
                      {activity.user.username}
                      <EliteBadge isElite={!!activity.user.isElite} />
                    </span>
                    <span className="home-activity__verb">{actionText}</span>
                  </span>
                </Link>

                <ResultCard
                  card={feedCardToTile(activity)}
                  isHovered={hoveredKey === key}
                  onHover={() => setHoveredKey(key)}
                  onLeave={() => setHoveredKey(null)}
                  isMobile={isMobile}
                  isMenuOpen={openMenuKey === key}
                  onOpenMenu={() => {
                    setOpenMenuKey(key);
                    setHoveredKey(key);
                  }}
                  onCloseMenu={() => setOpenMenuKey(null)}
                  origin="home"
                  meta={
                    <>
                      {activity.pointValue !== null ? (
                        <Points points={activity.pointValue} type="small" />
                      ) : (
                        <span className="result-card-unpriced">???</span>
                      )}
                      <time
                        className="home-activity__time"
                        dateTime={activity.createdAt}
                        title={formatAccessibleTime(activity.createdAt)}
                      >
                        {formatRelativeTime(activity.createdAt)}
                      </time>
                    </>
                  }
                />
              </article>
            );
          })}
        </div>
      )}
    </section>
  );
};

export default RecentActivitySection;
